import { RadioGroup } from "@headlessui/react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import {
  ConnectionIcon,
  SpeedIcon,
  Subtract,
  VoiceOptionCall,
} from "@/assets/icons/icons";
import { RoamingCallExplanation } from "@/components/Portal/Cockpit/DeviceManagement/Roaming/ShowRoamingOptions/RoamingUsage/RoamingCallExplanation";
import { TOTAL_DAYS_IN_YEAR } from "@/utils/constants";
import { getTariffType } from "@/utils/deviceUtils";
import {
  getIncludedFeaturesRateSelection,
  TransformedProduct,
} from "@/utils/tariffUtils";
import { getCurrency } from "@/utils/translationHelpers";

const getDiscountedValue = ({
  value,
  discountPercent,
}: {
  value?: string;
  discountPercent: number;
}) => {
  if (!value) {
    return;
  } else {
    const valueAmount = parseFloat(value.replace("CHF", "").trim());
    const discount = (discountPercent / 100) * valueAmount;
    const discountedValue = valueAmount - discount;

    return getCurrency(discountedValue, "CHF");
  }
};

export const MobileAboCard = ({
  value,
  product,
  tribeDiscount,
  disabled,
}: {
  value?: string;
  product: TransformedProduct;
  tribeDiscount?: number;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  const tariffType = getTariffType(product.id);

  if (!product.id) return null;

  const includedFeatures = getIncludedFeaturesRateSelection(
    t,
    tariffType,
    product.minContractDur || 30,
  );
  const isYearlyAbo = product.minContractDur === TOTAL_DAYS_IN_YEAR;
  const roamingDataFeature =
    includedFeatures.length > 3 ? includedFeatures[3].name : "-";
  const roamingDataWithInterval =
    isYearlyAbo && roamingDataFeature !== "-"
      ? `${roamingDataFeature} ${t("portal:mobileAbo.features.perMonth")}`
      : roamingDataFeature;

  const iconClassName = "size-6 flex-shrink-0 text-primary-100";

  return (
    <RadioGroup.Option
      disabled={disabled}
      defaultChecked={value === product.id.toString()}
      value={product.id.toString()}
    >
      {({ checked }) => (
        <>
          {product.recommended && (
            <div className="absolute">
              <div className="absolute z-10 ml-4 text-xs font-semibold text-white">
                {t("Onboarding.sections.rate-selection.recommended")}
              </div>
              <Subtract />
            </div>
          )}
          <div
            className={clsx(
              "icon-tile flex h-full w-[250px] px-6 py-4 text-black xs:w-[280px] xsPlus:w-[300px]",
              disabled && "opacity-50",
              checked && "selected",
            )}
          >
            <div className="mt-2 flex justify-between">
              <h2 className="text-secondary-100">{product.baseDisplayValue}</h2>
              <div className="mt-px pt-px">
                {checked ? (
                  <svg
                    width="33"
                    height="32"
                    viewBox="0 0 33 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M16.5 6c-5.52 0-10 4.48-10 10s4.48 10 10 10 10-4.48 10-10-4.48-10-10-10zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-5-8a5 5 0 1 1 10 0 5 5 0 0 1-10 0z"
                      fill="#0014FF"
                    />
                  </svg>
                ) : (
                  <svg
                    width="33"
                    height="32"
                    viewBox="0 0 33 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.5 16c0-5.52 4.48-10 10-10s10 4.48 10 10-4.48 10-10 10-10-4.48-10-10zm2 0c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8-8 3.58-8 8z"
                      fill="#0014FF"
                    />
                  </svg>
                )}
              </div>
            </div>
            <div
              className={clsx(
                "flex flex-grow flex-col gap-4 text-base",
                checked ? "text-primary-100" : "",
              )}
            >
              <p className="font-semibold">{t("countries.CHE")}</p>

              <p className="flex flex-row gap-2">
                <ConnectionIcon className={iconClassName} />
                {includedFeatures[0]?.name || "-"}
              </p>
              <p className="flex flex-row gap-2">
                <SpeedIcon className={iconClassName} />
                {includedFeatures[2]?.name || "-"}
              </p>
              <p className="flex flex-row gap-2">
                <VoiceOptionCall className={iconClassName} />
                {includedFeatures[1]?.name || "-"}
              </p>

              <p className="font-semibold">
                {t("portal:mobileAbo.features.international")}
              </p>

              <p className="flex flex-row gap-2">
                <ConnectionIcon className={iconClassName} />
                {roamingDataWithInterval}
              </p>
              <div className="flex flex-row gap-2">
                <VoiceOptionCall className={iconClassName} />
                <span className="inline-flex items-end">
                  {includedFeatures.length > 4 ? includedFeatures[4].name : "-"}
                  {includedFeatures.length > 4 && <RoamingCallExplanation />}
                </span>
              </div>
            </div>

            <hr className="border-primary-100" />
            <div
              className={clsx(
                "text-lg font-semibold",
                checked ? "text-primary-100" : "text-black",
              )}
            >
              {tribeDiscount && tribeDiscount > 0 ? (
                <>
                  <p className="text-xs line-through">{product.chargeFlat}</p>
                  <p>
                    {getDiscountedValue({
                      value: product.chargeFlat,
                      discountPercent: tribeDiscount,
                    })}
                  </p>
                </>
              ) : (
                <p>{product.chargeFlat}</p>
              )}
            </div>
          </div>
        </>
      )}
    </RadioGroup.Option>
  );
};
